// Bloc du bas de l'écran de séance pendant un test de force : trois montées guidées,
// puis le calcul du 1RM par la formule de Brzycki sur la série maximale.
//
// Ne dessine QUE le bloc du bas. Le titre du mouvement, sa catégorie, les muscles
// sollicités, le schéma musculaire, les instructions techniques et les vidéos restent
// rendus par `ExerciseView` exactement comme pour une séance normale — le test change ce
// que le client doit faire, pas ce qu'il doit savoir du mouvement.
//
// Tout le calcul vit dans `@/lib/strengthTest` (module pur). Ici il n'y a que de la
// saisie, de l'affichage et un appel d'enregistrement délégué au parent.

import { useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import { computeChargeKg, type AthleteLevel, type AthleteOneRm } from "@/lib/programs"
import {
  estimateOneRmBrzycki,
  referenceOneRmFromLift,
  BRZYCKI_RELIABLE_REPS,
  type TestSetSpec,
  type TestedLift,
} from "@/lib/strengthTest"
import { MOVEMENT_LABELS } from "@/lib/strengthStandards"
import type { SessionExercise } from "@/types/session"
import { formatFr } from "@/lib/utils"
import { T, I, TimerCountdown, TimerButton } from "@/components/session/sessionUi"

/** Clé du chrono de repos qui suit la série `setIndex` de l'exercice `exerciseIndex`. */
export function strengthTestRestKey(sessionId: string, exerciseIndex: number, setIndex: number): string {
  return `${sessionId}|test-${exerciseIndex}-${setIndex}`
}

// ── Résultat remonté au parent pour enregistrement ──────────

export interface StrengthTestResult {
  /** 1RM dans l'unité d'`athlete_profiles` — c'est lui qui part dans `rm_history`. */
  referenceOneRmKg: number
  /** 1RM du mouvement réellement exécuté, avant conversion par le ratio. */
  liftOneRmKg: number
  /** Charge manipulée, dans l'unité du mouvement (kg barre, kg par haltère, kg système). */
  liftWeightKg: number
  repsDone: number
  isMaxEffort: boolean
}

// ── Sémantique du champ « poids soulevé » ───────────────────

/**
 * Un « poids soulevé » ne veut pas dire la même chose selon le mouvement, et demander au
 * client de faire la conversion de tête serait le meilleur moyen d'enregistrer un chiffre
 * faux. Quatre formes, toutes rendues avec le même champ :
 *   — `plain`       : la charge sur la barre ou la machine ;
 *   — `perDumbbell` : le poids d'UN haltère (la table de ratios raisonne par haltère) ;
 *   — `added`       : le lest accroché au poids de corps (traction lestée, dips) ;
 *   — `assist`      : l'assistance retranchée au poids de corps (traction assistée).
 *
 * Le choix entre `added` et `assist` suit la charge proposée — exactement la règle
 * d'affichage déjà utilisée par `computeChargeKg` (`diff = total − poids de corps`), pour
 * que le libellé du champ soit celui que le client vient de lire sur la tuile Charge.
 */
type LoadInputKind = "plain" | "perDumbbell" | "added" | "assist"

interface LoadInputShape {
  kind: LoadInputKind
  label: string
  hint: string | null
  /** Valeur pré-remplie, dans l'unité du champ (pas celle du mouvement). */
  prefill: number | null
}

function resolveLoadInput(
  lift: TestedLift,
  proposedLiftKg: number | null,
  bodyweightKg: number | null
): LoadInputShape {
  if (lift.perDumbbell) {
    return {
      kind: "perDumbbell",
      label: "Poids par haltère (kg)",
      hint: "Le poids d'UN seul haltère, pas la somme des deux.",
      prefill: proposedLiftKg,
    }
  }

  if (lift.bodyweightBased) {
    if (bodyweightKg == null) {
      // Sans poids de corps connu on ne peut ni composer ni décomposer le système :
      // on demande directement la charge totale plutôt que d'inventer la moitié du calcul.
      return {
        kind: "plain",
        label: "Charge totale soulevée (kg)",
        hint: "Ton poids de corps plus le lest.",
        prefill: proposedLiftKg,
      }
    }
    const diff = proposedLiftKg == null ? 0 : proposedLiftKg - bodyweightKg
    if (diff < 0) {
      return {
        kind: "assist",
        label: "Assistance (kg)",
        hint: `Retranchée à ton poids de corps (${Math.round(bodyweightKg)} kg).`,
        prefill: Math.round(-diff),
      }
    }
    return {
      kind: "added",
      label: "Lest ajouté (kg)",
      hint: `S'ajoute à ton poids de corps (${Math.round(bodyweightKg)} kg). 0 si tu tires à vide.`,
      prefill: Math.round(diff),
    }
  }

  return { kind: "plain", label: "Poids soulevé (kg)", hint: null, prefill: proposedLiftKg }
}

/** Valeur saisie → charge dans l'unité du mouvement, celle qu'attend Brzycki. */
function liftWeightFromInput(
  shape: LoadInputShape,
  value: number,
  bodyweightKg: number | null
): number | null {
  switch (shape.kind) {
    case "added":
      return bodyweightKg == null ? null : bodyweightKg + value
    case "assist":
      return bodyweightKg == null ? null : bodyweightKg - value
    default:
      return value
  }
}

// ── Petits éléments d'interface ─────────────────────────────

function Tile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ background:T.sf,border:`1px solid ${T.bd}`,borderRadius:12,padding:"12px 10px" }}>
      <div style={{ fontSize:10,color:T.t3,fontWeight:600,letterSpacing:0.8,textTransform:"uppercase" as const,marginBottom:6 }}>{label}</div>
      {children}
    </div>
  )
}

const tileValue: React.CSSProperties = { fontSize:16,fontWeight:700,color:"#fff",wordBreak:"break-word" }
const tileSub: React.CSSProperties = { fontSize:10,color:T.t3,marginTop:3,lineHeight:1.35 }

function FieldLabel({ text }: { text: string }) {
  return <div style={{ fontSize:11,color:T.t2,fontWeight:600,marginBottom:6 }}>{text}</div>
}

const inputStyle: React.CSSProperties = {
  width:"100%",background:"rgba(255,255,255,0.04)",border:`1px solid ${T.bd}`,borderRadius:10,
  padding:"12px 14px",color:"#fff",fontSize:17,fontWeight:700,outline:"none",
  fontVariantNumeric:"tabular-nums",
}

// ── Composant principal ─────────────────────────────────────

export function StrengthTestBlock({
  ex,
  sessionId,
  exerciseIndex,
  lift,
  protocol,
  setIndex,
  resting,
  oneRm,
  referenceIsEstimated,
  bodyweightKg,
  level,
  previousReferenceOneRmKg,
  saving,
  onRestDone,
  onRecord,
}: {
  ex: SessionExercise
  /** Identifiant de la séance — préfixe la clé de persistance du repos (effacée en fin de séance). */
  sessionId: string
  /** Index de l'exercice dans la séance — sert de base à la clé de persistance du repos. */
  exerciseIndex: number
  lift: TestedLift
  protocol: TestSetSpec[]
  /** 0, 1 ou 2 — index de la série en cours dans `protocol`. */
  setIndex: number
  /** true pendant le repos qui suit la série `setIndex`. */
  resting: boolean
  /** 1RM de référence du client, complété par l'estimation initiale si besoin. */
  oneRm: AthleteOneRm | null
  /** true quand le 1RM de référence de CE mouvement est une estimation, pas une mesure. */
  referenceIsEstimated: boolean
  bodyweightKg: number | null
  level: AthleteLevel
  /** Dernier 1RM connu pour ce mouvement, pour afficher l'écart. Null au premier test. */
  previousReferenceOneRmKg: number | null
  saving: boolean
  onRestDone: () => void
  onRecord: (result: StrengthTestResult) => void
}) {
  const spec = protocol[setIndex]

  // Charge de la série, calculée par le MÊME code que toutes les autres charges de l'app
  // (`computeChargeKg`) — la série max comprise, dont le pourcentage est l'inverse exact
  // de Brzycki à 3 répétitions. Le 1RM passé ici est déjà le 1RM effectif : valeur réelle
  // du client, ou estimation initiale s'il n'a jamais testé ce mouvement.
  const charge = useMemo(
    () => computeChargeKg(ex.canonicalName, `${spec.percentOfOneRm}% 1RM`, oneRm, bodyweightKg, level),
    [ex.canonicalName, spec.percentOfOneRm, oneRm, bodyweightKg, level]
  )
  const proposed = charge.movements[0] ?? null
  const proposedLiftKg = proposed?.weightKg ?? null

  const shape = useMemo(
    () => resolveLoadInput(lift, proposedLiftKg, bodyweightKg),
    [lift, proposedLiftKg, bodyweightKg]
  )

  // ── Formulaire de la série max ────────────────────────────
  const [weightDraft, setWeightDraft] = useState("")
  const [repsDraft, setRepsDraft] = useState(String(spec.reps))
  const [toFailure, setToFailure] = useState<boolean | null>(null)
  const [result, setResult] = useState<StrengthTestResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [retrying, setRetrying] = useState(false)

  // Le pré-remplissage suit la charge proposée, et se réinitialise quand on change de
  // mouvement (`ex.canonicalName`) — sans quoi le deuxième mouvement du test arriverait
  // avec la saisie du premier déjà dans les champs.
  useEffect(() => {
    setWeightDraft(shape.prefill != null ? String(Math.round(shape.prefill)) : "")
    setRepsDraft(String(spec.reps))
    setToFailure(null)
    setResult(null)
    setError(null)
    setRetrying(false)
  }, [ex.canonicalName, shape.prefill, spec.reps])

  const compute = () => {
    const weight = parseFloat(weightDraft.trim().replace(",", "."))
    const reps = parseInt(repsDraft.trim(), 10)

    if (!Number.isFinite(weight) || weight < 0) {
      setError("Indique le poids que tu as soulevé.")
      return
    }
    if (!Number.isFinite(reps) || reps < 1) {
      setError("Indique au moins une répétition réalisée.")
      return
    }
    if (toFailure == null) {
      setError("Dis-nous si tu es allé à l'échec musculaire.")
      return
    }

    const liftWeightKg = liftWeightFromInput(shape, weight, bodyweightKg)
    if (liftWeightKg == null || liftWeightKg <= 0) {
      setError(
        shape.kind === "assist"
          ? "L'assistance ne peut pas dépasser ton poids de corps."
          : "Cette charge ne permet pas de calculer un 1RM."
      )
      return
    }

    const liftOneRmKg = estimateOneRmBrzycki(liftWeightKg, reps)
    const referenceOneRmKg = liftOneRmKg == null ? null : referenceOneRmFromLift(liftOneRmKg, lift.ratio)
    if (liftOneRmKg == null || referenceOneRmKg == null) {
      // Seul cas atteignable : 37 répétitions ou plus, où la formule n'est plus définie.
      setError("Ce nombre de répétitions sort du domaine de la formule. Refais une série plus lourde.")
      return
    }

    setError(null)
    setResult({
      referenceOneRmKg: Math.round(referenceOneRmKg * 2) / 2,
      liftOneRmKg,
      liftWeightKg,
      repsDone: reps,
      isMaxEffort: toFailure,
    })
  }

  const startLighterAttempt = () => {
    // Chemin de secours : la charge proposée n'est pas passée. On ne repart pas de la
    // série 1 — on rouvre simplement le formulaire sur une proposition plus légère, que
    // le client reste libre de réécrire.
    const current = parseFloat(weightDraft.trim().replace(",", "."))
    const base = Number.isFinite(current) && current > 0 ? current : shape.prefill ?? 0
    const lighter = Math.max(0, Math.round(base * 0.9))
    setWeightDraft(lighter > 0 ? String(lighter) : "")
    setRepsDraft(String(spec.reps))
    setResult(null)
    setError(null)
    setRetrying(true)
  }

  // ── Repos entre séries ────────────────────────────────────
  if (resting) {
    const isLast = setIndex >= protocol.length - 1
    return (
      <div style={{ marginBottom:14 }}>
        <div style={{ display:"inline-flex",alignItems:"center",gap:6,background:`${T.gn}10`,border:`1px solid ${T.gn}28`,borderRadius:20,padding:"5px 14px",marginBottom:14 }}>
          <span style={{ color:T.gn,display:"flex" }}>{I.chk}</span>
          <span style={{ color:T.gn,fontSize:13,fontWeight:600 }}>Série {spec.index}/{protocol.length} terminée</span>
        </div>
        <TimerCountdown
          sec={spec.restSec}
          restKey={strengthTestRestKey(sessionId, exerciseIndex, setIndex)}
          onDone={onRestDone}
          label={isLast ? "Récupère avant le mouvement suivant" : `Série ${spec.index + 1} dans :`}
        />
      </div>
    )
  }

  // ── Tuiles de la série en cours ───────────────────────────
  const chargeTile = spec.maxEffort ? (
    <Tile label="Charge">
      <div style={tileValue}>{proposed ? `Charge proposée ${proposed.weight}` : "Charge libre"}</div>
      <div style={{ ...tileSub, color:T.or, fontWeight:600 }}>Vise ton maximum</div>
    </Tile>
  ) : (
    <Tile label="Charge">
      <div style={tileValue}>{proposed?.weight ?? "—"}</div>
      {proposed?.detail && <div style={tileSub}>{proposed.detail}</div>}
    </Tile>
  )

  return (
    <div style={{ marginBottom:14 }}>
      {/* Repère de progression dans le test */}
      <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap" as const }}>
        <span style={{ padding:"4px 12px",background:T.cyD,border:`1px solid ${T.cy}33`,borderRadius:20,fontSize:12,fontWeight:700,color:T.cy }}>
          Série {spec.index}/{protocol.length}
        </span>
        {spec.maxEffort && (
          <span style={{ padding:"4px 12px",background:`${T.or}10`,border:`1px solid ${T.or}30`,borderRadius:20,fontSize:12,fontWeight:700,color:T.or }}>
            Série maximale
          </span>
        )}
        {referenceIsEstimated && (
          <span style={{ fontSize:11,color:T.t3 }}>Charges de départ estimées — ajuste si besoin</span>
        )}
      </div>

      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8 }}>
        <Tile label="Série"><div style={tileValue}>{spec.index}/{protocol.length}</div></Tile>
        <Tile label="Reps">
          <div style={tileValue}>{spec.maxEffort ? `${spec.reps} max` : spec.reps}</div>
          {spec.maxEffort && <div style={tileSub}>Objectif — va au bout</div>}
        </Tile>
        {chargeTile}
      </div>

      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14 }}>
        <Tile label="Tempo">
          <div style={{ ...tileValue, fontSize:18 }}>{ex.tempo}</div>
          {ex.tempoExplain && <div style={{ ...tileSub, fontSize:11 }}>{ex.tempoExplain}</div>}
        </Tile>
        <Tile label="Repos après cette série">
          <div style={{ ...tileValue, fontSize:18 }}>
            {spec.restSec >= 60 ? `${Math.round(spec.restSec / 60)} min` : `${spec.restSec} sec`}
          </div>
          <div style={{ ...tileSub, fontSize:11 }}>Le chrono démarre à la validation.</div>
        </Tile>
      </div>

      {/* ── Série maximale : formulaire de saisie puis résultat ── */}
      {spec.maxEffort && (
        <div style={{ background:T.sf,border:`1px solid ${result ? T.gn + "40" : T.bd}`,borderRadius:14,padding:"16px 16px 18px",transition:"border-color 0.2s" }}>
          {result ? (
            <ResultPanel
              result={result}
              lift={lift}
              exerciseName={ex.canonicalName}
              previousReferenceOneRmKg={previousReferenceOneRmKg}
              saving={saving}
              onConfirm={() => onRecord(result)}
              onRedo={() => { setResult(null); setError(null) }}
            />
          ) : (
            <>
              <div style={{ fontSize:13,fontWeight:700,color:T.t1,marginBottom:4 }}>Ta série maximale</div>
              <div style={{ fontSize:12,color:T.t3,lineHeight:1.5,marginBottom:14 }}>
                Peu importe que tu aies fait plus ou moins de répétitions que prévu : entre ce que tu as
                réellement fait, le 1RM se calcule dessus.
              </div>

              {retrying && (
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:12,color:T.or,fontWeight:600,marginBottom:8 }}>
                    Nouvelle tentative — récupère avant de repartir.
                  </div>
                  <TimerButton label="Repos" sec={spec.restSec} accent={T.or} />
                </div>
              )}

              <div style={{ marginBottom:14 }}>
                <FieldLabel text={shape.label} />
                <input
                  type="number" inputMode="decimal" step="0.5" min="0"
                  value={weightDraft}
                  onChange={(e) => setWeightDraft(e.target.value)}
                  placeholder="—"
                  style={inputStyle}
                />
                {shape.hint && <div style={{ fontSize:11,color:T.t3,marginTop:6,lineHeight:1.4 }}>{shape.hint}</div>}
              </div>

              <div style={{ marginBottom:14 }}>
                <FieldLabel text="Répétitions réalisées" />
                <input
                  type="number" inputMode="numeric" step="1" min="1"
                  value={repsDraft}
                  onChange={(e) => setRepsDraft(e.target.value)}
                  placeholder="—"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom:14 }}>
                <FieldLabel text="Je suis allé à l'échec musculaire" />
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8 }}>
                  {[
                    { value: true, label: "Oui" },
                    { value: false, label: "Non" },
                  ].map((opt) => {
                    const on = toFailure === opt.value
                    return (
                      <motion.button
                        key={opt.label}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => { setToFailure(opt.value); setError(null) }}
                        style={{
                          padding:"12px 0",borderRadius:10,cursor:"pointer",fontSize:14,fontWeight:700,
                          background: on ? T.cyD : "rgba(255,255,255,0.03)",
                          border: `1px solid ${on ? T.cy + "66" : T.bd}`,
                          color: on ? T.cy : T.t2,
                          transition:"all 0.2s",
                        }}
                      >
                        {opt.label}
                      </motion.button>
                    )
                  })}
                </div>
                <div style={{ fontSize:11,color:T.t3,marginTop:6,lineHeight:1.4 }}>
                  Ça ne change pas le calcul — ça dit au coach à quel point s'y fier.
                </div>
              </div>

              {error && (
                <div style={{ fontSize:12,color:"#f87171",fontWeight:600,marginBottom:12 }}>{error}</div>
              )}

              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={compute}
                style={{
                  width:"100%",padding:"15px 0",border:"none",borderRadius:12,fontWeight:700,fontSize:15,
                  color:"#fff",cursor:"pointer",background:`linear-gradient(135deg,#0369a1,${T.cy})`,
                }}
              >
                Calculer mon 1RM
              </motion.button>

              <button
                onClick={startLighterAttempt}
                style={{
                  width:"100%",marginTop:10,padding:"10px 0",background:"none",border:"none",
                  color:T.t3,fontSize:12,fontWeight:600,cursor:"pointer",textDecoration:"underline",
                }}
              >
                Je n'ai réussi aucune répétition — retenter plus léger
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ── Panneau de résultat ─────────────────────────────────────

function ResultPanel({
  result,
  lift,
  exerciseName,
  previousReferenceOneRmKg,
  saving,
  onConfirm,
  onRedo,
}: {
  result: StrengthTestResult
  lift: TestedLift
  exerciseName: string
  previousReferenceOneRmKg: number | null
  saving: boolean
  onConfirm: () => void
  onRedo: () => void
}) {
  const delta =
    previousReferenceOneRmKg != null && previousReferenceOneRmKg > 0
      ? result.referenceOneRmKg - previousReferenceOneRmKg
      : null
  const decimals = result.referenceOneRmKg % 1 === 0 ? 0 : 1

  return (
    <div style={{ textAlign:"center" as const }}>
      <div style={{ fontSize:10,color:T.t3,letterSpacing:2,fontWeight:600 }}>
        1RM · {MOVEMENT_LABELS[lift.movement].toUpperCase()}
      </div>
      <div style={{ fontSize:44,fontWeight:800,color:"#fff",lineHeight:1.1,marginTop:6,fontVariantNumeric:"tabular-nums" as const }}>
        {formatFr(result.referenceOneRmKg, decimals)}
        <span style={{ fontSize:18,fontWeight:600,color:T.t3 }}> kg</span>
      </div>

      {delta != null && (
        <div style={{ fontSize:13,marginTop:8,fontWeight:600,color: delta > 0 ? "#4ade80" : delta < 0 ? "#f87171" : T.t2 }}>
          {delta === 0
            ? "Identique à ton dernier test"
            : `${delta > 0 ? "+" : "−"}${formatFr(Math.abs(delta), Math.abs(delta) % 1 === 0 ? 0 : 1)} kg depuis ton dernier test`}
        </div>
      )}
      {delta == null && (
        <div style={{ fontSize:12,color:T.t3,marginTop:8 }}>Premier test enregistré pour ce mouvement.</div>
      )}

      <div style={{ fontSize:11,color:T.t3,marginTop:12,lineHeight:1.6,textAlign:"left" as const }}>
        <div>
          Calculé avec la formule de Brzycki sur {result.repsDone} répétition
          {result.repsDone > 1 ? "s" : ""}.
        </div>
        {!lift.isDirectMeasure && (
          <div style={{ marginTop:4 }}>
            Estimé depuis {exerciseName} ({formatFr(result.liftOneRmKg, 1)} kg sur ce mouvement).
          </div>
        )}
        {result.repsDone > BRZYCKI_RELIABLE_REPS && (
          <div style={{ marginTop:4,color:T.or }}>
            Au-delà de {BRZYCKI_RELIABLE_REPS} répétitions la formule perd en précision — à confirmer au prochain test.
          </div>
        )}
        {!result.isMaxEffort && (
          <div style={{ marginTop:4,color:T.or }}>
            Série arrêtée avant l'échec : ta vraie valeur est probablement plus haute. Enregistrée quand même,
            et signalée au coach.
          </div>
        )}
      </div>

      <motion.button
        whileTap={{ scale: 0.98 }}
        disabled={saving}
        onClick={onConfirm}
        style={{
          width:"100%",marginTop:16,padding:"15px 0",border:"none",borderRadius:12,fontWeight:700,
          fontSize:15,color:"#fff",cursor:saving ? "default" : "pointer",opacity:saving ? 0.6 : 1,
          background:`linear-gradient(135deg,#15803d,${T.gn})`,
        }}
      >
        {saving ? "Enregistrement…" : "Enregistrer et continuer →"}
      </motion.button>

      <button
        onClick={onRedo}
        disabled={saving}
        style={{
          width:"100%",marginTop:10,padding:"10px 0",background:"none",border:"none",
          color:T.t3,fontSize:12,fontWeight:600,cursor:saving ? "default" : "pointer",textDecoration:"underline",
        }}
      >
        Corriger ma saisie
      </button>
    </div>
  )
}
